import { FormProvider } from "react-hook-form";

import {
  FormField,
  SectionDivider,
  inputCls,
  readOnlyCls,
} from "../../../components";
import { useSuitabilityForm } from "./hooks/useSuitabilityForm";
import { flattenSuitability } from "./SuitabilityForm.rules";
import { SuitabilityFormProps } from "./SuitabilityForm.types";
import { Button } from "@/components/Button/Button";

export function SuitabilityForm({
  profileData = [],
  portfolios = [],
  pendingOrders = [],
  modelsData = [],
  onDocumentCreated,
  onClose,
}: SuitabilityFormProps) {
  const form = useSuitabilityForm(
    profileData,
    portfolios,
    pendingOrders,
    modelsData
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (data: Parameters<typeof flattenSuitability>[0]) => {
    onDocumentCreated?.(flattenSuitability(data));
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
        <SectionDivider title="Customer profile" />

        <div className="space-y-3">
          <FormField label="Knowledge and experience:">
            <input
              type="text"
              {...register("dpInvType")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="Financial situation:">
            <input
              type="text"
              {...register("dpFinancialSituation")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="Time horizon:">
            <input
              type="text"
              {...register("dpInvTime")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="Risk tolerance:">
            <input
              type="text"
              {...register("dpRisktolerance")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="Risk preference and investment goals:">
            <input
              type="text"
              {...register("dpRiskpreferenceAndInvestmentGoals")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="ESG preference:">
            <input
              type="text"
              {...register("dpEsgPreference")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>
        </div>

        <SectionDivider title="Portfolio" />

        <div className="space-y-3">
          <FormField label="Stock part before:">
            <input
              type="text"
              {...register("dpInitStockPart")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>

          <FormField label="Stock part after:">
            <input
              type="text"
              {...register("dpFinStockPart")}
              readOnly
              className={readOnlyCls}
            />
          </FormField>
        </div>

        <SectionDivider title="Advice" />

        <div className="space-y-3">
          <FormField label="System advice:">
            <textarea
              {...register("dpSystemAdvice")}
              readOnly
              rows={5}
              className={`${readOnlyCls} resize-none`}
            />
          </FormField>

          <FormField label="Use input from customer agreement:">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none pt-1.5">
              <input
                type="checkbox"
                {...register(
                  "useInputFromCustomerAgreementInSuitabilityStatement"
                )}
                className="accent-blue-600 w-4 h-4"
              />
              <span className="text-gray-600 dark:text-gray-400">
                Include in suitability statement
              </span>
            </label>
          </FormField>

          <FormField label="Comment advisor:">
            <textarea
              {...register("dpAdvAdvice")}
              rows={5}
              className={`${inputCls} resize-y`}
            />
          </FormField>

          <FormField label="ESG comment:">
            <textarea
              {...register("dpAdvAdviceESG")}
              rows={3}
              className={`${inputCls} resize-y`}
            />
          </FormField>
        </div>

        {Object.keys(errors).length > 0 && (
          <p className="text-xs text-red-500 pt-1">
            Please fill in all required fields.
          </p>
        )}

        <div className="flex justify-end gap-3 pt-6">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          )}
          <Button type="submit" variant="default" size="md">
            Add to document
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
